import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast, SimpleTable, Input, Button } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createCorporateGroup, deleteCorporateGroup, fetchCorporateGroups, updateCorporateGroup, } from '../../../api/CorporateGroupAdminApi';
import { createCorporateGroupColumns } from './tableColumns';
import styles from './CorporateGroups.module.scss';
const DEFAULT_FILTERS = {
    is_active: '',
};
const toNumberOrZero = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};
const getCorporateGroupErrorMessage = (error) => {
    const payload = error?.response?.body;
    const nonFieldMessage = payload?.nonFieldErrors?.[0] ?? payload?.non_field_errors?.[0];
    if (typeof nonFieldMessage === 'string' && nonFieldMessage.trim()) {
        return nonFieldMessage;
    }
    const fieldMessage = payload?.name?.[0] ??
        payload?.corporateMembershipId?.[0] ??
        payload?.corporate_membership_id?.[0] ??
        payload?.discountPercentage?.[0] ??
        payload?.discount_percentage?.[0];
    if (typeof fieldMessage === 'string' && fieldMessage.trim()) {
        return fieldMessage;
    }
    return 'Failed to save corporate group';
};
const CorporateGroups = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editName, setEditName] = useState('');
    const [editAbbreviation, setEditAbbreviation] = useState('');
    const [editMembershipId, setEditMembershipId] = useState('');
    const [editDiscountPercentage, setEditDiscountPercentage] = useState('0');
    const [editIsActive, setEditIsActive] = useState(true);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addAbbreviation, setAddAbbreviation] = useState('');
    const [addMembershipId, setAddMembershipId] = useState('');
    const [addDiscountPercentage, setAddDiscountPercentage] = useState('0');
    const [addIsActive, setAddIsActive] = useState(true);
    const [isSavingAdd, setIsSavingAdd] = useState(false);
    const [addErrorMessage, setAddErrorMessage] = useState('');
    const latestRequestIdRef = useRef(0);
    const loadCorporateGroups = useCallback(async (search, activeFilters = DEFAULT_FILTERS) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchCorporateGroups({
                search: search || undefined,
                is_active: activeFilters.is_active === '' ? undefined : activeFilters.is_active === 'true',
            });
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            const data = response.body;
            const groupsArray = Array.isArray(data) ? data : data.results || [];
            const transformedGroups = groupsArray.map((item) => ({
                id: item.id,
                name: item.name,
                abbreviation: item.abbreviation,
                corporate_membership_id: item.corporateMembershipId ?? item.corporate_membership_id,
                discount_percentage: item.discountPercentage ?? item.discount_percentage,
                is_active: !!(item.isActive ?? item.is_active),
                created_at: item.createdAt ?? item.created_at,
                created_at_display: item.createdAtDisplay ?? item.created_at_display,
            }));
            setGroups(transformedGroups);
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Error loading corporate groups:', error);
            showToast({ message: 'Failed to load corporate groups', variant: 'error', noBorder: true });
            setGroups([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadCorporateGroups(searchTerm, appliedFilters);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadCorporateGroups, searchTerm, appliedFilters]);
    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };
    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    const runBulkUpdate = async (selectedRows, isActive) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => updateCorporateGroup(id, { is_active: isActive })));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIds[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setGroups((prev) => prev.map((item) => (successfulIds.includes(item.id)
                    ? { ...item, is_active: isActive }
                    : item)));
                showToast({
                    message: `Updated ${successfulIds.length} corporate group${successfulIds.length === 1 ? '' : 's'} as ${isActive ? 'active' : 'inactive'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error bulk updating corporate groups for IDs:', failedIds);
                showToast({
                    message: `Failed to update ${failedIds.length} corporate group${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error running bulk update for corporate groups:', error);
            showToast({ message: 'Failed to update selected corporate groups', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleBulkDelete = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected corporate group${selectedIds.length === 1 ? '' : 's'}?`)) {
            return;
        }
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => deleteCorporateGroup(id)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIds[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setGroups((prev) => prev.filter((item) => !successfulIds.includes(item.id)));
                showToast({
                    message: `Deleted ${successfulIds.length} corporate group${successfulIds.length === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error deleting corporate groups for IDs:', failedIds);
                showToast({
                    message: `Failed to delete ${failedIds.length} corporate group${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error deleting selected corporate groups:', error);
            showToast({ message: 'Failed to delete selected corporate groups', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleOpenEdit = (group) => {
        setEditingGroup(group);
        setEditName(group.name || '');
        setEditAbbreviation(group.abbreviation || '');
        setEditMembershipId(String(group.corporate_membership_id || ''));
        setEditDiscountPercentage(String(toNumberOrZero(group.discount_percentage)));
        setEditIsActive(!!group.is_active);
        setIsEditOpen(true);
    };
    const handleCloseEdit = () => {
        setIsEditOpen(false);
        setEditingGroup(null);
        setEditName('');
        setEditAbbreviation('');
        setEditMembershipId('');
        setEditDiscountPercentage('0');
        setEditIsActive(true);
    };
    const handleOpenAdd = () => {
        setAddName('');
        setAddAbbreviation('');
        setAddMembershipId('');
        setAddDiscountPercentage('0');
        setAddIsActive(true);
        setAddErrorMessage('');
        setIsAddOpen(true);
    };
    const handleCloseAdd = () => {
        setIsAddOpen(false);
        setAddName('');
        setAddAbbreviation('');
        setAddMembershipId('');
        setAddDiscountPercentage('0');
        setAddIsActive(true);
        setAddErrorMessage('');
    };
    const handleSaveEdit = async () => {
        if (!editingGroup)
            return;
        if (!editName.trim() || !editMembershipId.trim()) {
            showToast({ message: 'Name and membership ID are required', variant: 'error', noBorder: true });
            return;
        }
        setIsSavingEdit(true);
        try {
            await updateCorporateGroup(editingGroup.id, {
                name: editName.trim(),
                abbreviation: editAbbreviation.trim() || '',
                corporate_membership_id: Number(editMembershipId),
                discount_percentage: toNumberOrZero(editDiscountPercentage),
                is_active: editIsActive,
            });
            setGroups((prev) => prev.map((item) => (item.id === editingGroup.id
                ? {
                    ...item,
                    name: editName.trim(),
                    abbreviation: editAbbreviation.trim() || null,
                    corporate_membership_id: Number(editMembershipId),
                    discount_percentage: toNumberOrZero(editDiscountPercentage),
                    is_active: editIsActive,
                }
                : item)));
            showToast({ message: 'Corporate group updated successfully', variant: 'success', noBorder: true });
            handleCloseEdit();
        }
        catch (error) {
            console.error('Error updating corporate group:', error);
            showToast({ message: getCorporateGroupErrorMessage(error), variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingEdit(false);
        }
    };
    const handleSaveAdd = async () => {
        if (!addName.trim() || !addMembershipId.trim()) {
            setAddErrorMessage('Name and membership ID are required');
            showToast({ message: 'Name and membership ID are required', variant: 'error', noBorder: true });
            return;
        }
        setIsSavingAdd(true);
        setAddErrorMessage('');
        try {
            await createCorporateGroup({
                name: addName.trim(),
                abbreviation: addAbbreviation.trim() || '',
                corporate_membership_id: Number(addMembershipId),
                discount_percentage: toNumberOrZero(addDiscountPercentage),
                is_active: addIsActive,
            });
            showToast({ message: 'Corporate group created successfully', variant: 'success', noBorder: true });
            handleCloseAdd();
            loadCorporateGroups(searchTerm, appliedFilters);
        }
        catch (error) {
            console.error('Error creating corporate group:', error);
            const message = getCorporateGroupErrorMessage(error);
            setAddErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingAdd(false);
        }
    };
    const columns = createCorporateGroupColumns();
    const bulkActions = [
        {
            label: 'Activate',
            action: (selectedRows) => runBulkUpdate(selectedRows, true),
        },
        {
            label: 'Deactivate',
            action: (selectedRows) => runBulkUpdate(selectedRows, false),
        },
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDelete(selectedRows),
        },
    ];
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "corporate-group-is-active", children: "Is Active" }), _jsxs("select", { id: "corporate-group-is-active", value: filters.is_active, onChange: (event) => setFilters((prev) => ({ ...prev, is_active: event.target.value })), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Active" }), _jsx("option", { value: "false", children: "Inactive" })] })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: handleApplyFilters }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleResetFilters })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by name, abbreviation, or membership ID", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: columns, data: groups, rowHeight: "60px", tableHeading: "Corporate Groups", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px',
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Corporate groups per page", pageSize: 20, onRowClick: (item) => handleOpenEdit(item), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Corporate Group", onClick: handleOpenAdd }) })] }), isEditOpen && editingGroup && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseEdit }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Edit Corporate Group" }) }), _jsxs("div", { className: styles.slideOutContent, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-corporate-group-name", children: "Name" }), _jsx("input", { id: "edit-corporate-group-name", value: editName, onChange: (event) => setEditName(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-corporate-group-abbreviation", children: "Abbreviation" }), _jsx("input", { id: "edit-corporate-group-abbreviation", value: editAbbreviation, onChange: (event) => setEditAbbreviation(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-corporate-group-membership-id", children: "Membership ID" }), _jsx("input", { id: "edit-corporate-group-membership-id", type: "number", min: "1", value: editMembershipId, onChange: (event) => setEditMembershipId(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-corporate-group-discount", children: "Discount Percentage" }), _jsx("input", { id: "edit-corporate-group-discount", type: "number", min: "0", max: "100", step: "0.01", value: editDiscountPercentage, onChange: (event) => setEditDiscountPercentage(event.target.value) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsActive, onChange: (event) => setEditIsActive(event.target.checked) }), "Is Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseEdit }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingEdit ? 'Saving...' : 'Save Changes', onClick: handleSaveEdit, disabled: isSavingEdit })] })] })] })), isAddOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseAdd }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Add Corporate Group" }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!addErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: addErrorMessage })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-corporate-group-name", children: "Name" }), _jsx("input", { id: "add-corporate-group-name", value: addName, onChange: (event) => {
                                                    setAddName(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                } })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-corporate-group-abbreviation", children: "Abbreviation" }), _jsx("input", { id: "add-corporate-group-abbreviation", value: addAbbreviation, onChange: (event) => setAddAbbreviation(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-corporate-group-membership-id", children: "Membership ID" }), _jsx("input", { id: "add-corporate-group-membership-id", type: "number", min: "1", value: addMembershipId, onChange: (event) => {
                                                    setAddMembershipId(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                } })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-corporate-group-discount", children: "Discount Percentage" }), _jsx("input", { id: "add-corporate-group-discount", type: "number", min: "0", max: "100", step: "0.01", value: addDiscountPercentage, onChange: (event) => setAddDiscountPercentage(event.target.value) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: addIsActive, onChange: (event) => setAddIsActive(event.target.checked) }), "Is Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseAdd }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingAdd ? 'Saving...' : 'Create Corporate Group', onClick: handleSaveAdd, disabled: isSavingAdd })] })] })] }))] }));
};
export default CorporateGroups;
